import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

interface Props {
  from?: 'server' | 'client';
  startAt: number;
  columnWidth: number;
  duration?: keyof typeof PATHS;
}

function Arrow({
  from = 'server',
  startAt,
  columnWidth,
  duration = 4,
}: Props) {
  const rotation = from === 'server' ? 0 : 180;

  const viewBoxWidth = duration * 20 - 10;
  const width = duration * columnWidth - 10;

  return (
    <Wrapper
      layout="position"
      style={{
        gridColumn: `${startAt + 1} /  span ${duration}`,
        gridRow: `2 / span 3`,
      }}
    >
      <Svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height="150"
        fill="none"
        viewBox={`0 0 ${viewBoxWidth} 158`}
        preserveAspectRatio="none"
        style={{
          transform: `rotateX(${rotation}deg)`,
        }}
      >
        <path fill="var(--color-text)" d={PATHS[duration]} />
      </Svg>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  align-self: center;
  justify-self: center;
  animation: fadeIn 500ms both;
  animation-delay: 200ms;
  z-index: 2;
`;

const Svg = styled(motion.svg)`
  display: block;
  overflow: visible;
  vector-effect: non-scaling-stroke;

  path {
    vector-effect: non-scaling-stroke;
  }
`;

const PATHS = {
  3: 'M1.04165 0.000867832C0.489847 -0.0221361 0.0238717 0.406542 0.000867832 0.958348C-0.0221361 1.51015 0.406542 1.97613 0.958348 1.99913L1.04165 0.000867832ZM49.7179 150.691C50.1024 150.295 50.0926 149.662 49.6962 149.277L43.2354 143.012C42.8389 142.627 42.2058 142.637 41.8214 143.033C41.4369 143.43 41.4466 144.063 41.843 144.447L47.586 150.017L42.0165 155.76C41.632 156.156 41.6417 156.789 42.0382 157.174C42.4346 157.558 43.0677 157.549 43.4522 157.152L49.7179 150.691ZM0.958348 1.99913C4.99276 2.16732 7.80638 4.35962 9.79949 8.32164C11.823 12.344 12.9428 18.1035 13.4917 25.1293C14.5898 39.1834 13.3786 57.7403 13.0734 76.4156C12.7697 94.9997 13.3664 113.68 18.1755 127.67C20.5858 134.682 24.0779 140.591 29.1127 144.714C34.1646 148.851 40.6868 151.123 49.0153 150.995L48.9847 148.995C41.0632 149.117 35.0186 146.966 30.3799 143.167C25.7241 139.354 22.4018 133.813 20.0669 127.02C15.3857 113.402 14.7691 95.0507 15.0731 76.4483C15.3757 57.9371 16.5934 39.1518 15.4856 24.9735C14.9317 17.883 13.7899 11.8036 11.5862 7.42286C9.35202 2.98171 5.97275 0.206438 1.04165 0.000867832L0.958348 1.99913Z',
  4: 'M1.02941 0.000432697C0.477369 -0.0158124 0.0166778 0.41854 0.000432697 0.970586C-0.0158124 1.52263 0.41854 1.98332 0.970586 1.99957L1.02941 0.000432697ZM69.7147 150.695C70.101 150.3 70.0941 149.667 69.6994 149.28L63.2669 142.986C62.8722 142.599 62.2391 142.606 61.8528 143.001C61.4665 143.396 61.4734 144.029 61.8681 144.415L67.5859 150.01L61.9906 155.728C61.6043 156.123 61.6111 156.756 62.0059 157.142C62.4006 157.529 63.0337 157.522 63.42 157.127L69.7147 150.695ZM0.970586 1.99957C7.02922 2.17786 11.1163 4.52244 13.9171 8.45261C16.7633 12.4464 18.3403 18.1619 19.115 25.1614C20.6678 39.1901 18.9539 57.6994 18.5207 76.4088C18.0904 94.9961 18.9329 113.737 25.7727 127.783C29.207 134.836 34.1608 140.723 41.2234 144.806C48.279 148.885 57.3679 151.121 69.0108 150.995L68.9892 148.995C57.6113 149.118 48.8972 146.932 42.2244 143.075C35.5587 139.221 30.8588 133.66 27.5708 126.908C20.966 113.344 20.0896 95.0543 20.5202 76.4551C20.948 57.978 22.675 39.1451 21.1029 24.9413C20.3152 17.8246 18.6883 11.7013 15.5459 7.29189C12.3581 2.81888 7.67193 0.195903 1.02941 0.000432697L0.970586 1.99957Z',
  5: 'M1.02273 0.000258434C0.470591 -0.0122968 0.0128137 0.425125 0.000258434 0.977267C-0.0122968 1.52941 0.425125 1.98719 0.977267 1.99974L1.02273 0.000258434ZM89.713 150.696C90.1002 150.302 90.0949 149.669 89.7012 149.282L83.2842 142.972C82.8904 142.584 82.2573 142.59 81.87 142.983C81.4828 143.377 81.4881 144.01 81.8819 144.398L87.5858 150.007L81.9765 155.711C81.5892 156.105 81.5945 156.738 81.9883 157.125C82.3821 157.512 83.0152 157.507 83.4025 157.113L89.713 150.696ZM0.977267 1.99974C8.99235 2.182 14.3856 4.59308 18.0351 8.55021C21.7134 12.5386 23.7414 18.2264 24.7394 25.1932C25.7364 32.1541 25.6897 40.273 25.3165 49.0202C24.9456 57.7156 24.2464 67.1148 23.9681 76.402C23.4109 94.9983 24.5002 113.794 33.3762 127.878C42.3207 142.072 59.0009 151.246 89.0084 150.995L88.9916 148.995C59.4157 149.243 43.5227 140.228 35.0683 126.812C26.5454 113.288 25.4102 95.0521 25.9672 76.4619C26.2461 67.1559 26.9386 57.9228 27.3147 49.1054C27.6886 40.3397 27.7441 32.0649 26.7192 24.9096C25.6951 17.7601 23.5769 11.6091 19.5053 7.19429C15.4049 2.74825 9.44443 0.191761 1.02273 0.000258434L0.977267 1.99974Z',
  6: 'M1.01853 0.000171603C0.466335 -0.0100595 0.0104027 0.429285 0.000171603 0.981475C-0.0100595 1.53366 0.429285 1.9896 0.981475 1.99983L1.01853 0.000171603ZM109.712 150.697C110.1 150.304 110.095 149.671 109.702 149.283L103.295 142.963C102.902 142.575 102.269 142.579 101.881 142.972C101.493 143.366 101.497 143.999 101.891 144.387L107.586 150.005L101.968 155.7C101.58 156.093 101.584 156.726 101.977 157.114C102.37 157.502 103.004 157.498 103.391 157.105L109.712 150.697ZM0.981475 1.99983C10.917 2.18392 17.6248 4.62653 22.1467 8.62166C26.6718 12.6196 29.1463 18.2938 30.3646 25.2245C31.5846 32.1643 31.5283 40.2656 31.0706 49.0105C30.6161 57.6923 29.757 67.1101 29.4155 76.3952C28.7312 95.0065 30.07 113.848 40.9817 127.957C51.9336 142.118 72.3026 151.245 109.007 150.995L108.993 148.995C72.5724 149.243 52.9652 140.182 42.5638 126.734C32.1224 113.233 30.7311 95.0439 31.4142 76.4687C31.7565 67.1606 32.6055 57.9462 33.0678 49.1151C33.5268 40.347 33.596 32.0547 32.3344 24.8782C31.0713 17.6927 28.4571 11.5281 23.4709 7.12284C18.4816 2.71479 11.2555 0.189843 1.01853 0.000171603L0.981475 1.99983Z',
};

export default Arrow;
